import * as React from 'react';
import { Redirect } from 'react-router';
import * as _ from 'underscore';

import { Favorites } from '~content/resources/Favorites';
import { DataSources } from '~src/datasource/resources/Datasources';

import { RouterError } from '../../app/spaces/RouterHelpers/RouterError';
import { SpacesSiteUrls } from '../../app/spaces/SpacesSiteUrls';
import { Filter } from '../../ts/helpers/Filter';
import { IDataSourceInfo } from '../models/IDataSourceInfo';

/**
 * We need a RedirectToFavoritedDatasourceProvider because:
 * 1. we need to keep track of the idForFavoriting which comes from the url so we can correctly remove the DS if it has been deleted. This gets
 *    removed when redirecting to datasourcePath because it is not needed for the path.
 * 2. we can redirect to datasourcePath, which handles redirecting to /askData or away from /askData if the NLP feature is turned off. If we just
 *    use a to datasourcePath redirect inside RootSpaceRouter, the 'previousUrl' becomes '/datasource/id' instead of '/redirect_to_datasource/idForFavoriting/id'
 *    and we lose the information that we were redirected from a 'redirect_to_datasource' or 'redirect_to_removed_datasource' url (and then can't remove
 *    the deleted favorite or show the correct error page).
 */

export interface RedirectToFavoritedDatasourceProviderProps {
  readonly datasourceId?: string;  // optional because /redirect_to_removed_datasource/ only has the id for favoriting
  readonly idForFavoriting: string;
  readonly urls: SpacesSiteUrls;
}

export interface RedirectToFavoritedDatasourceProviderState {
  readonly redirectDatasourceId: string;
  readonly hasError: boolean;
}

const InitialState: RedirectToFavoritedDatasourceProviderState = {
  redirectDatasourceId: undefined,
  hasError: false
};

export class RedirectToFavoritedDatasourceProvider extends React.PureComponent<RedirectToFavoritedDatasourceProviderProps, RedirectToFavoritedDatasourceProviderState> {
  private unmounted: boolean = false;

  public constructor(props: RedirectToFavoritedDatasourceProviderProps) {
    super(props);
    this.state = InitialState;
  }

  public render(): JSX.Element {
    if (this.state.hasError) {
      return <RouterError errorType={'datasource-removed-favorite'} urls={this.props.urls} />;
    } else if (!_.isUndefined(this.state.redirectDatasourceId)) {
      return <Redirect to={this.props.urls.datasourcePath(this.state.redirectDatasourceId)} />;
    }

    return null;
  }

  public async componentDidMount() {
    if (_.isUndefined(this.props.datasourceId)) {
      this.setToErrorStateAndRemoveFromFavorites();
      return;
    }

    try {
      const datasource: IDataSourceInfo = await DataSources.all()
        .withFilters([Filter.fieldEq('id', this.props.datasourceId)])
        .getFirst();
      this.updateState({ redirectDatasourceId: datasource.id });

    } catch (e) {
      this.setToErrorStateAndRemoveFromFavorites();
    }
  }

  private setToErrorStateAndRemoveFromFavorites() {
    Favorites.remove(this.props.idForFavoriting, 'datasource');
    this.updateState({ hasError: true });
  }

  public componentWillUnmount() {
    this.unmounted = true;
  }

  private updateState<K extends keyof RedirectToFavoritedDatasourceProviderState>(newState: Pick<RedirectToFavoritedDatasourceProviderState, K>): void {
    if (!this.unmounted) {
      this.setState(newState);
    }
  }
}
